import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, GripVertical, Link2, Video, FileText, Code, Save, X, Eye, EyeOff, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const CustomTabs = () => {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const [users, setUsers] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    icon: 'link',
    description: '',
    links: [],
    embed_code: '',
    is_active: true,
    show_to_all: true,
    allowed_user_ids: [],
    only_activated: false,
    order: 0
  });

  const [newLink, setNewLink] = useState({ title: '', url: '', type: 'link' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tabsRes, usersRes] = await Promise.all([
        api.get('/admin/tabs/all'),
        api.get('/admin/users')
      ]);
      setTabs(tabsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Failed to load tabs');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (tab = null) => {
    if (tab) {
      setEditingTab(tab);
      setFormData({
        title: tab.title || '',
        icon: tab.icon || 'link',
        description: tab.description || '',
        links: tab.links || [],
        embed_code: tab.embed_code || '',
        is_active: tab.is_active !== false,
        show_to_all: tab.show_to_all !== false,
        allowed_user_ids: tab.allowed_user_ids || [],
        only_activated: tab.only_activated || false,
        order: tab.order || 0
      });
    } else {
      setEditingTab(null);
      setFormData({
        title: '',
        icon: 'link',
        description: '',
        links: [],
        embed_code: '',
        is_active: true,
        show_to_all: true,
        allowed_user_ids: [],
        only_activated: false,
        order: tabs.length
      });
    }
    setShowEditor(true);
  };

  const addLink = () => {
    if (!newLink.title || !newLink.url) return;
    setFormData({
      ...formData,
      links: [...formData.links, { ...newLink }]
    });
    setNewLink({ title: '', url: '', type: 'link' });
  };

  const removeLink = (index) => {
    setFormData({
      ...formData,
      links: formData.links.filter((_, i) => i !== index)
    });
  };

  const saveTab = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      if (editingTab) {
        await api.put(`/admin/tabs/update/${editingTab.id}`, formData);
        toast.success('Tab updated');
      } else {
        await api.post('/admin/tabs/create', formData);
        toast.success('Tab created');
      }
      setShowEditor(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save tab');
    }
  };

  const deleteTab = async (tabId) => {
    if (!confirm('Delete this tab?')) return;
    try {
      await api.delete(`/admin/tabs/delete/${tabId}`);
      toast.success('Tab deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleUser = (userId) => {
    const current = formData.allowed_user_ids || [];
    if (current.includes(userId)) {
      setFormData({ ...formData, allowed_user_ids: current.filter(id => id !== userId) });
    } else {
      setFormData({ ...formData, allowed_user_ids: [...current, userId] });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="custom-tabs-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Link2 className="w-6 h-6 text-primary" />
            Custom Dashboard Tabs
          </h1>
          <p className="text-muted-foreground">Add custom tabs with links, files, and embed codes to user dashboards</p>
        </div>
        <button onClick={() => openEditor()} className="px-4 py-2 bg-primary text-black font-semibold rounded-xl flex items-center gap-2">
          <Plus className="w-5 h-5" /> Add Tab
        </button>
      </div>

      {/* Tabs List */}
      <div className="space-y-3">
        {tabs.map((tab) => (
          <div key={tab.id} className="glass-card p-4 flex items-center gap-4">
            <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{tab.title}</h3>
                {!tab.is_active && <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded">Hidden</span>}
                {tab.only_activated && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">Activated Only</span>}
                {!tab.show_to_all && <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Specific Users</span>}
              </div>
              <p className="text-sm text-muted-foreground">{tab.links?.length || 0} links • {tab.description || 'No description'}</p>
            </div>
            <button onClick={() => openEditor(tab)} className="p-2 hover:bg-white/10 rounded-lg"><Edit className="w-4 h-4" /></button>
            <button onClick={() => deleteTab(tab.id)} className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {tabs.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Link2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No custom tabs yet</p>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="glass-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{editingTab ? 'Edit Tab' : 'Create Tab'}</h2>
              <button onClick={() => setShowEditor(false)} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Tab Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="My Custom Tab"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Optional description"
                />
              </div>

              {/* Links */}
              <div className="p-4 bg-white/5 rounded-xl">
                <label className="block text-sm font-medium mb-3">Links & Files</label>
                <div className="space-y-2 mb-4">
                  {formData.links.map((link, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-black/20 rounded-lg">
                      {link.type === 'file' ? <FileText className="w-4 h-4" /> : link.type === 'embed' ? <Code className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                      <span className="flex-1 truncate">{link.title}</span>
                      <span className="text-xs text-muted-foreground truncate max-w-[150px]">{link.url}</span>
                      <button onClick={() => removeLink(i)} className="p-1 hover:bg-red-500/20 rounded"><X className="w-4 h-4 text-red-400" /></button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <select value={newLink.type} onChange={(e) => setNewLink({ ...newLink, type: e.target.value })} className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm">
                    <option value="link">Link</option>
                    <option value="file">File</option>
                    <option value="embed">Embed</option>
                  </select>
                  <input type="text" value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm" placeholder="Title" />
                  <input type="text" value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm" placeholder="URL or Embed Code" />
                  <button onClick={addLink} className="px-3 py-2 bg-primary/20 text-primary rounded-lg text-sm"><Plus className="w-4 h-4 mx-auto" /></button>
                </div>
              </div>

              {/* Embed Code */}
              <div>
                <label className="block text-sm mb-1">Embed Code (Optional)</label>
                <textarea
                  value={formData.embed_code}
                  onChange={(e) => setFormData({ ...formData, embed_code: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl h-24 font-mono text-sm"
                  placeholder="<iframe>...</iframe>"
                />
              </div>

              {/* Visibility */}
              <div className="p-4 bg-white/5 rounded-xl space-y-3">
                <label className="block text-sm font-medium">Visibility</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                    <Eye className="w-4 h-4" /> Active
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.only_activated} onChange={(e) => setFormData({ ...formData, only_activated: e.target.checked })} />
                    <Shield className="w-4 h-4" /> Activated Users Only
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.show_to_all} onChange={(e) => setFormData({ ...formData, show_to_all: e.target.checked })} />
                    <Users className="w-4 h-4" /> Show to All
                  </label>
                </div>

                {!formData.show_to_all && (
                  <div className="mt-3 p-3 bg-black/20 rounded-lg max-h-40 overflow-y-auto">
                    <p className="text-xs text-muted-foreground mb-2">Select specific users:</p>
                    <div className="space-y-1">
                      {users.filter(u => u.role !== 'admin').map(user => (
                        <label key={user.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white/5 p-1 rounded">
                          <input type="checkbox" checked={(formData.allowed_user_ids || []).includes(user.id)} onChange={() => toggleUser(user.id)} />
                          {user.name} (@{user.username})
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button onClick={saveTab} className="w-full py-3 bg-primary text-black font-semibold rounded-xl flex items-center justify-center gap-2">
                <Save className="w-5 h-5" /> Save Tab
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTabs;
